import { useEffect } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { AlertTriangle } from 'lucide-react';
import { FlowCanvas } from './FlowCanvas';
import { NodeDetailsModal } from '@/components/modals/NodeDetailsModal';
import { Button } from '@/components/ui/button';
import { useCanvasStore } from '@/stores';
import { useCanvasData } from '@/hooks/use-canvas-data';

interface ValuePropositionCanvasProps {
  projectId: string;
}

export function ValuePropositionCanvas({ projectId }: ValuePropositionCanvasProps) {
  const { hydrate, setActiveProjectId } = useCanvasStore();
  const { data, isLoading, isError, isFetching, refetch } = useCanvasData(projectId);

  useEffect(() => {
    setActiveProjectId(projectId);
  }, [projectId, setActiveProjectId]);

  // Load server truth into the store whenever the canvas query data changes:
  // initial load, and the rollback refetch after a failed persist (see
  // use-canvas-mutations). A successful persist writes the store's own arrays
  // into the cache, so this re-runs with identical references — a no-op.
  useEffect(() => {
    if (data) {
      hydrate(data.nodes, data.edges);
    }
  }, [data, hydrate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-muted-foreground">Loading canvas...</div>
      </div>
    );
  }

  // Only block when no graph ever loaded. isError also fires when a background
  // refetch fails while cached data is live — including the rollback refetch
  // after a failed persist — and tearing down a rendered graph for a transient
  // blip loses more than the stale data costs.
  if (isError && data === undefined) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="rounded-lg border bg-card p-5 flex items-center gap-3 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span className="text-destructive">
            Couldn't load the graph. Nothing's lost — check your connection and try again.
          </span>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Retrying...' : 'Try again'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div className="w-full h-full">
        <FlowCanvas projectId={projectId} />
        <NodeDetailsModal projectId={projectId} />
      </div>
    </ReactFlowProvider>
  );
}
